import { GoodThing } from '../types/good-things';
import { firestoreWrapper } from './firestoreWrapper';
import { where, Timestamp } from 'firebase/firestore';

// Cache configuration
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const RANDOM_GOOD_THING_CACHE_KEY = 'randomGoodThingCache';

interface CacheEntry {
  goodThing: GoodThing | null;
  timestamp: number;
}

// Get the date from 7 days ago
const getLastWeekDate = (): Date => {
  const now = new Date();
  const lastWeek = new Date(now);
  lastWeek.setDate(now.getDate() - 7);
  lastWeek.setHours(0, 0, 0, 0);
  return lastWeek;
};

// Convert a Date to a Firestore Timestamp
const dateToFirestoreTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};

export const randomGoodThingService = {
  /**
   * Get a random good thing from the last 7 days
   * This function will:
   * 1. Check cache first
   * 2. If cache is valid, return cached good thing
   * 3. If cache is invalid, fetch good things from the last 7 days
   * 4. Randomly select one and cache it
   * 5. Return the selected good thing
   * 
   * If any step fails, it will return null (no retries, no error messages)
   */
  async getRandomGoodThing(): Promise<GoodThing | null> {
    try {
      // Check cache first
      const cached = this.loadCache();
      const now = Date.now();
      
      if (cached && now - cached.timestamp < CACHE_TTL) {
        
        return cached.goodThing;
      }
      
      // Cache is invalid or doesn't exist, fetch new data
      
      
      // Get the date from 7 days ago
      const lastWeekDate = getLastWeekDate();
      const lastWeekTimestamp = dateToFirestoreTimestamp(lastWeekDate);
      
      // Query for good things from the last 7 days using timestamp field
      const goodThingsQuery = firestoreWrapper.query(
        firestoreWrapper.collection('goodThings'),
        where('timestamp', '>=', lastWeekTimestamp),
        firestoreWrapper.orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await firestoreWrapper.getDocs(goodThingsQuery);
      
      // If no good things found, return null
      if (querySnapshot.empty) {
        
        this.saveCache(null);
        return null;
      }
      
      // Convert query snapshot to array of good things
      const goodThings = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as GoodThing[];
      
      // Randomly select one good thing
      const randomIndex = Math.floor(Math.random() * goodThings.length);
      const randomGoodThing = goodThings[randomIndex];
      
      // Cache the result
      this.saveCache(randomGoodThing);
      
      return randomGoodThing;
    } catch (error) {
      
      return null;
    }
  },
  
  /**
   * Load cache from localStorage
   */
  loadCache(): CacheEntry | null {
    try {
      const cached = localStorage.getItem(RANDOM_GOOD_THING_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      
    }
    return null;
  },
  
  /**
   * Save cache to localStorage
   */
  saveCache(goodThing: GoodThing | null): void {
    try {
      const cacheEntry: CacheEntry = {
        goodThing,
        timestamp: Date.now()
      };
      localStorage.setItem(RANDOM_GOOD_THING_CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (e) {
      
    }
  },
  
  /**
   * Clear cache
   */
  clearCache(): void {
    try {
      localStorage.removeItem(RANDOM_GOOD_THING_CACHE_KEY);
    } catch (e) {
      
    }
  }
};
